import { Box, Paper } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Icon from '@material-ui/core/Icon';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import { swalApiError, showSuccessSwal, showErrorSwal } from 'app/utility/Swalutility';
import DeleteConfirmationDialog from 'allGlobalComponents/DeleteConfirmationDoalog';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import AddCallReply from './AddCallReply';

const useStyles = makeStyles(theme => ({
	root: {
		maxWidth: '100%',
		padding: '0px'
	},
	content: {
		padding: '0px'
	},
	headerClass: {
		backgroundColor: theme.palette.primary.light
	}
}));

// Shared call chatbot CRUD manager. Used in two contexts:
//   - Company profile (an admin managing a specific client) -> pass `clientId`.
//   - The client's own call chatbot page                    -> omit `clientId`.
// The ONLY difference between the two is whether `clientId` is sent in the
// API payloads; everything else (endpoints, UI, IVR builder) is identical.
function CallChatbotManager({ clientId, title = 'Chatbot Calls' }) {
	const theme = useTheme();
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const [chatBotData, setChatBotData] = useState(null);
	const [selected, setSelected] = useState([]);
	const [data, setData] = useState([]);
	const [searchVal, setSearchVal] = useState('');
	const [isLoading, setLoading] = useState(true);
	const [confirmation, setConfirmationType] = useState(null);
	const [confirmationData, setConfirmationData] = useState(null);
	const [confirmLoading, setConfirmLoading] = useState(false);

	// Merge `clientId` into an API payload only when it is provided.
	const withClient = payload => (clientId != null ? { ...payload, clientId } : payload);

	useEffect(() => {
		getPaginatedData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientId]);

	const getPaginatedData = () => {
		setLoading(true);
		CoreHttpHandler.request(
			'chatbot_calls',
			'callChatbotGetAll',
			withClient({}),
			response => {
				setLoading(false);
				setData(response?.data?.data?.chatbots || []);
			},
			error => {
				setLoading(false);
				swalApiError(error, 'Failed to load call chatbots.');
			}
		);
	};

	function showMessage({ msg, success }) {
		if (msg) {
			if (success) {
				showSuccessSwal('Success', msg);
			} else {
				showErrorSwal('Error', msg);
			}
		}
	}

	function closeHandler({ msg, success }) {
		setOpen(false);
		setChatBotData(null);
		showMessage({ msg, success });
	}

	const handleCreate = () => {
		setChatBotData(null);
		setOpen(true);
	};

	const updateChatBot = n => {
		setChatBotData(n);
		setOpen(true);
	};

	const cloneChatBot = n => {
		const update_params = withClient({
			name: `${n.name}_${uuidv4()}`,
			body: n.body || {},
			active: n.active !== false
		});
		CoreHttpHandler.request(
			'chatbot_calls',
			'callChatbotCreate',
			update_params,
			() => {
				closeHandler({ msg: 'Successfully Saved', success: true });
				getPaginatedData();
			},
			error => {
				swalApiError(error, 'Failed to clone call chatbot.');
			}
		);
	};

	const saveHandler = params => {
		CoreHttpHandler.request(
			'chatbot_calls',
			'callChatbotCreate',
			withClient(params),
			() => {
				closeHandler({ msg: 'Successfully Saved', success: true });
				getPaginatedData();
			},
			error => {
				setOpen(false);
				swalApiError(error, 'Failed to create call chatbot.');
			}
		);
	};

	const updateHandler = params => {
		if (chatBotData) {
			CoreHttpHandler.request(
				'chatbot_calls',
				'callChatbotUpdate',
				withClient({ ...params, id: Number(chatBotData.id) }),
				() => {
					closeHandler({ msg: 'Successfully Updated', success: true });
					getPaginatedData();
				},
				error => {
					setOpen(false);
					swalApiError(error, 'Failed to update call chatbot.');
				}
			);
		}
	};

	const handleConfirmationDialogClose = () => {
		setConfirmationData(null);
		setConfirmationType(null);
	};

	const openConfirmationDialogue = (type, row) => {
		setConfirmationData(row);
		setConfirmationType(type);
	};

	const handleConfirmationSubmit = () => {
		if (!confirmationData || !confirmation) return;

		setConfirmLoading(true);

		if (confirmation === 'delete') {
			CoreHttpHandler.request(
				'chatbot_calls',
				'callChatbotDelete',
				withClient({ id: Number(confirmationData.id) }),
				() => {
					setConfirmLoading(false);
					showMessage({ msg: 'Successfully Deleted', success: true });
					handleConfirmationDialogClose();
					getPaginatedData();
				},
				error => {
					setConfirmLoading(false);
					handleConfirmationDialogClose();
					swalApiError(error, 'Failed to delete call chatbot.');
				}
			);
		} else {
			const update_params = withClient({
				id: Number(confirmationData.id),
				name: confirmationData.name,
				body: confirmationData.body || {},
				active: !confirmationData.active
			});
			CoreHttpHandler.request(
				'chatbot_calls',
				'callChatbotUpdate',
				update_params,
				() => {
					setConfirmLoading(false);
					showMessage({ msg: 'Successfully Updated', success: true });
					handleConfirmationDialogClose();
					getPaginatedData();
				},
				error => {
					setConfirmLoading(false);
					handleConfirmationDialogClose();
					swalApiError(error, 'Failed to update call chatbot.');
				}
			);
		}
	};

	const headCells = [
		{ id: 'id', label: 'ID', numeric: true, tooltip: true },
		{ id: 'name', label: 'Name', numeric: false, tooltip: true },
		{ id: 'enabled_action', label: 'Enabled', numeric: true },
		{ id: 'delete_action', label: 'Delete', numeric: true },
		{ id: 'dt', label: 'Created', numeric: false, dt: true },
		{ id: 'edit_action', label: 'Edit', numeric: true },
		{ id: 'clone_action', label: 'Clone', numeric: true }
	];

	const filteredData = (data || []).filter(o =>
		searchVal ? Object.values(o).join(' ').toLowerCase().includes(searchVal.toLowerCase()) : true
	);

	const tableRows = filteredData.map(n => ({
		...n,
		enabled_action: (
			<div
				style={{ cursor: 'pointer' }}
				onClick={e => {
					e.stopPropagation();
					openConfirmationDialogue('enabled', n);
				}}
			>
				{n.active === true ? (
					<Icon style={{ color: theme.palette.secondary?.value || theme.palette.secondary?.main }}>
						check_circle
					</Icon>
				) : (
					<Icon>radio_button_unchecked</Icon>
				)}
			</div>
		),
		delete_action: (
			<div
				style={{ cursor: 'pointer' }}
				onClick={e => {
					e.stopPropagation();
					openConfirmationDialogue('delete', n);
				}}
			>
				{n.deleted === true ? <Icon color="secondary">restore</Icon> : <Icon>delete</Icon>}
			</div>
		),
		edit_action: (
			<div
				style={{ cursor: 'pointer' }}
				onClick={e => {
					e.stopPropagation();
					updateChatBot(n);
				}}
			>
				<Icon>edit</Icon>
			</div>
		),
		clone_action: (
			<div
				style={{ cursor: 'pointer' }}
				onClick={e => {
					e.stopPropagation();
					cloneChatBot(n);
				}}
			>
				<Icon>arrow_downward</Icon>
			</div>
		)
	}));

	return (
		<Card className={classes.root}>
			<CardContent className={classes.content} style={{ width: '100%' }}>
				{!open ? (
					<Typography
						variant="h2"
						style={{ display: 'flex', alignItems: 'center' }}
						className={`companyDetailHeader ${classes.headerClass}`}
					>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%'
							}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
								<Icon style={{ fontSize: 26 }}>reply_all</Icon>
								{title}
							</Box>
							<Paper className="flex items-center w-full max-w-sm ml-32 px-8 py-4" elevation={1}>
								<Icon color="action" fontSize="small">
									search
								</Icon>
								<div className="flex flex-1 mx-8">
									<FormFieldOmni
										placeholder="Search"
										name="search"
										variant="standard"
										value={searchVal}
										onChange={e => setSearchVal(e.target.value)}
										style={{
											border: 'none',
											color: theme.palette.text.primary,
											fontSize: '12px',
											height: '30px',
											margin: 0,
											padding: 0,
											width: '100%'
										}}
										InputProps={{ disableUnderline: true }}
									/>
								</div>
							</Paper>
							<div className="flex gap-12 items-center justify-center">
								<ButtonOmni size="small" color="primary" onClick={handleCreate} label="Create" />
							</div>
						</Box>
					</Typography>
				) : null}

				{open ? (
					<div style={{ backgroundColor: 'white' }}>
						<AddCallReply
							data={chatBotData}
							clientId={clientId}
							closeHandler={closeHandler}
							saveHandler={saveHandler}
							updateHandler={updateHandler}
						/>
					</div>
				) : (
					<div className="w-full flex flex-col">
						<TableOmniNewAnimated
							rows={tableRows}
							headCells={headCells}
							loading={isLoading}
							checkBox={false}
							selectedRows={selected}
							setSelectedRows={setSelected}
							rowKey="id"
							isLoadingPagination={false}
							perPageRows={10}
							rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
							fixedHeight
							maxTableHeight="calc(100vh - 280px)"
							emptyMessage="No Data Found!"
						/>
						{confirmation && confirmationData ? (
							<DeleteConfirmationDialog
								open={!!(confirmation && confirmationData)}
								onClose={handleConfirmationDialogClose}
								onConfirm={handleConfirmationSubmit}
								loading={confirmLoading}
								title={
									confirmation === 'delete'
										? 'Delete Chatbot'
										: confirmationData.active
										? 'Disable Chatbot'
										: 'Enable Chatbot'
								}
								message={
									confirmation === 'delete'
										? `Are you sure you want to delete the call chatbot "${confirmationData.name}"?`
										: confirmationData.active
										? `Are you sure you want to disable the call chatbot "${confirmationData.name}"?`
										: `Are you sure you want to enable the call chatbot "${confirmationData.name}"?`
								}
								confirmLabel={
									confirmation === 'delete'
										? 'Delete'
										: confirmationData.active
										? 'Disable'
										: 'Enable'
								}
							/>
						) : null}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

export default CallChatbotManager;
